import React, { useState, useEffect } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Container,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import ReportCarousel from "./ReportCarousel";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#00ff00",
    },
    secondary: {
      main: "#00bfff",
    },
    background: {
      default: "#0a0a0a",
      paper: "#1a1a1a",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      color: "#00ff00",
      fontWeight: "bold",
    },
    h6: {
      color: "#00bfff",
    },
  },
});

function PublicReportView() {
  const { jobId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`/api/reports/${jobId}`);
        if (response.data.success) {
          setReport(response.data.data);
        } else {
          setError(response.data.error || "Failed to load report");
        }
      } catch (err) {
        const body = err.response?.data;
        if (body?.error) {
          // The server explains a miss it can diagnose — e.g. a link whose
          // ticker is two symbols merged into one. Prefer it over a generic line.
          setError(body.detail ? `${body.error}. ${body.detail}` : body.error);
        } else if (err.response?.status === 404) {
          setError("Report not found. Please check the URL.");
        } else {
          setError("Failed to load report");
        }
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      fetchReport();
    }
  }, [jobId]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDecisionChip = (decision) => {
    if (!decision) return null;
    const colorMap = {
      BUY: { color: "success" },
      SELL: { color: "error" },
      HOLD: { color: "warning" },
      OUTPERFORM: { sx: { bgcolor: "#81c784", color: "#1b5e20" } },
      UNDERPERFORM: { sx: { bgcolor: "#ef9a9a", color: "#b71c1c" } },
    };
    const config = colorMap[decision] || { color: "default" };
    return (
      <Chip
        label={decision}
        color={config.color}
        sx={{ fontWeight: "bold", ml: 2, ...config.sx }}
      />
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={400}
        >
          <Box textAlign="center">
            <CircularProgress color="primary" size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading Report...
            </Typography>
          </Box>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      );
    }

    if (!report) {
      return (
        <Alert severity="warning" sx={{ mt: 4 }}>
          Report not found
        </Alert>
      );
    }

    if (report.status === "processing") {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={400}
        >
          <Box textAlign="center">
            <CircularProgress color="warning" size={60} />
            <Typography variant="h6" sx={{ mt: 2, color: "warning.main" }}>
              Analysis In Progress
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Analyzing {report.ticker}... Please check back in a few minutes.
            </Typography>
          </Box>
        </Box>
      );
    }

    if (report.status === "queued") {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={400}
        >
          <Box textAlign="center">
            <CircularProgress color="info" size={60} />
            <Typography variant="h6" sx={{ mt: 2, color: "info.main" }}>
              Analysis Queued
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Analysis for {report.ticker} is waiting to start. Please check
              back soon.
            </Typography>
          </Box>
        </Box>
      );
    }

    const reports = report.reports || {};

    return (
      <Box>
        <Paper sx={{ p: 3, mb: 3, backgroundColor: "background.paper" }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Typography
              variant="h4"
              component={RouterLink}
              to={`/stock/${report.ticker}`}
              color="secondary"
              sx={{ fontWeight: "bold", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              {report.ticker}
            </Typography>
            {getDecisionChip(report.final_decision)}
          </Box>

          <Box display="flex" gap={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="textSecondary">
                Recommendation
              </Typography>
              <Typography variant="body1">
                {report.final_decision || "-"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Created At
              </Typography>
              <Typography variant="body1">
                {formatDate(report.created_at)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Status
              </Typography>
              <Box>
                <Chip
                  label={report.status}
                  color={report.status === "completed" ? "success" : "default"}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        <ReportCarousel analysisData={reports} title="Analysis Reports" />
      </Box>
    );
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "background.default",
          py: 4,
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
          <Paper
            elevation={3}
            sx={{ p: { xs: 1.5, sm: 2, md: 4 }, backgroundColor: "background.paper" }}
          >
            <Box textAlign="center" mb={4}>
              <Typography
                variant="h4"
                gutterBottom
                component={RouterLink}
                to="/"
                sx={{
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.8, transition: "opacity 0.2s ease" },
                }}
              >
                Trading-Agents Report
              </Typography>
              <Typography variant="h6" color="textSecondary">
                Multi-Agents Trading Analysis
              </Typography>
            </Box>

            {renderContent()}

            <Box
              textAlign="center"
              mt={4}
              pt={3}
              borderTop="1px solid rgba(255,255,255,0.1)"
            >
              <Typography variant="caption" color="textSecondary">
                Powered by GPT
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default PublicReportView;
